'use client';

import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { ContentWrap, Css, Responsive } from '@/ui';
import {
	HeaderContentWrap,
	HeaderVideoOverlay,
	HeaderWrapper,
} from './Header.styles';
import { Color } from '../../../styles/Color';
import { FONT_FAMILY_BEBAS_NEUE } from '../../../styles/Font';
import defaultHeaderImage from '../../../assets/defaultHeaderImage.jpg';
import { Topbar } from '../top-bar/Topbar';
import { Z_INDEX_HEADER_TEXT } from '../../../styles/zIndex';
import { ISiteSectionEntry } from '../../../contentful/types/ISiteSection';

export interface IHeaderProps {
	backgroundImage?: string;
	backgroundVideo?: string;
	backgroundVideoFormat?: string;
	siteSection?: ISiteSectionEntry;
}

export default function HomeHeader({
	backgroundImage,
	backgroundVideo,
	backgroundVideoFormat,
	siteSection,
}: IHeaderProps) {
	return (
		<StyledHeaderWrapper
			backgroundImage={backgroundImage ?? defaultHeaderImage}
		>
			{backgroundVideo && (
				<StyledHeaderVideoOverlay>
					<StyledHeaderVideo loop controls={false} muted autoPlay playsInline>
						<source src={backgroundVideo} type={backgroundVideoFormat} />
					</StyledHeaderVideo>
				</StyledHeaderVideoOverlay>
			)}

			<HeaderContentWrap>
				<Topbar
					siteSection={siteSection}
					onBackgroundScheme={backgroundVideo ? 'dark' : 'light'}
				/>
				<HeaderContent>
					<PageSubtitle variant={backgroundVideo ? 'light' : 'dark'}>
						Welcome to
					</PageSubtitle>

					<PageTitle variant={backgroundVideo ? 'light' : 'dark'}>
						<>
							Cincinnati / <br /> <span>Northern Kentucky</span>
							<small>International Airport</small>
						</>
					</PageTitle>
				</HeaderContent>
			</HeaderContentWrap>
		</StyledHeaderWrapper>
	);
}

const HOME_HEADER_DESKTOP_HEIGHT = '855px';
const HOME_HEADER_MOBILE_HEIGHT = '440px';

const headerStyles = (variant: 'light' | 'dark') => css`
	color: ${variant === 'light' ? Color.PrimaryWhite : Color.PrimaryBlue};
	margin-bottom: 0;
	line-height: 85%;
`;

const StyledHeaderWrapper = styled(HeaderWrapper)`
	height: 100vh;
	max-height: ${HOME_HEADER_DESKTOP_HEIGHT};
	background-size: cover;
	background-position: bottom;
	background-image: ${({ backgroundImage }: { backgroundImage: string }) =>
		`url(${backgroundImage})`};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		height: ${HOME_HEADER_MOBILE_HEIGHT};
	}
`;

const StyledHeaderVideoOverlay = styled(HeaderVideoOverlay)`
	&::after {
		max-height: ${HOME_HEADER_DESKTOP_HEIGHT};
		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			max-height: ${HOME_HEADER_MOBILE_HEIGHT};
		}
	}
`;

const StyledHeaderVideo = styled.video`
	position: absolute;
	left: 0;
	z-index: 0;
	height: 100vh;
	max-height: ${HOME_HEADER_DESKTOP_HEIGHT};
	width: 100vw;
	object-fit: cover;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		height: ${HOME_HEADER_MOBILE_HEIGHT};
	}
`;

const HeaderContent = styled(ContentWrap)`
	margin-top: ${Css.pxToRem(140)};
	z-index: ${Z_INDEX_HEADER_TEXT};
	width: 100%;

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-top: ${Css.pxToRem(140)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: ${Css.pxToRem(40)};
	}
`;

const PageTitle = styled.h1<{ variant: 'light' | 'dark' }>`
	display: flex;
	flex-direction: column;

	font-family: ${FONT_FAMILY_BEBAS_NEUE}, Arial, Roboto, sans-serif;
	font-size: ${Css.pxToRem(80)};
	font-weight: 400;
	text-transform: uppercase;
	${({ variant }) => headerStyles(variant)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(40)};
	}

	small {
		${({ variant }) => headerStyles(variant)};
		font-size: ${Css.pxToRem(52)};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			font-size: ${Css.pxToRem(29)};
		}
	}
`;

const PageSubtitle = styled.div<{ variant: 'light' | 'dark' }>`
	font-size: ${Css.pxToRem(18)};
	font-weight: 400;
	color: ${({ variant }) =>
		variant === 'light' ? Color.PrimaryWhite : Color.PrimaryBlue};
	margin-bottom: ${Css.pxToRem(8)};
`;
